import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import i18next from 'i18next';
import { TFunction } from 'i18next';
import { useDashboard } from '@hub/features/account-dashboard/contexts';
import { useStore } from '@hub/helpers/use-store';
import type { ChipProps } from '@mui/material';
import { useTheme } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { trackMixpanel } from '@monorepo/client-common';
import { SystemIcon } from '@monorepo/nova-icons';
import {
  getScoreColor,
  Popover,
  Tooltip,
  TradeScoreCard,
} from '@monorepo/nova-ui';
import {
  AssetClass,
  ClosedPositionStatus,
  EventTypes,
  PositionSide,
  TradingSystem,
} from '@monorepo/types';
import { COLUMN_WIDTHS, DATE_FORMAT, TAG_COLORS } from './constants';
import {
  StyledCellBox,
  StyledPnLBox,
  StyledSideTag,
  StyledStatusBox,
  StyledStatusTag,
} from './styled';
import {
  createHandlePublishClick,
  formatBrokerDate,
  formatCurrencyStandard,
  formatInvalidReasons,
  getDefaultColumnConfig,
  getHeaderName,
  renderCurrencyWithTooltip,
} from './utils';
import { isCryptoAsset } from './utils/asset-detection.utils';

interface TradeScoreCellProps {
  score: number;
  metrics: { risk: number; precision: number; payoff: number };
  isMobile: boolean;
  tradeId?: string | number;
  symbol?: string;
  metadata?: Record<string, unknown>;
  userId: string;
  t: TFunction<'dashboard'>;
}

const TradeScoreCell: React.FC<TradeScoreCellProps> = ({
  score,
  metrics,
  isMobile,
  tradeId,
  symbol,
  metadata,
  userId,
  t,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const {
    uiStore: { sidebarStore },
  } = useStore();
  const { assetType } = useDashboard();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const isHoveringRef = React.useRef(false);

  // Check if this is an unsupported asset (crypto in Forex Dashboard)
  const isForexDashboard = assetType === AssetClass.Forex;
  const isCrypto = isCryptoAsset(symbol);
  const isUnsupportedAsset = isForexDashboard && isCrypto;

  const clearCloseTimeout = React.useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const scheduleClose = React.useCallback(() => {
    clearCloseTimeout();
    timeoutRef.current = setTimeout(() => {
      if (!isHoveringRef.current) {
        setAnchorEl(null);
      }
    }, 150);
  }, [clearCloseTimeout]);

  const handlePopoverOpen = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      clearCloseTimeout();
      isHoveringRef.current = true;
      setAnchorEl(event.currentTarget);

      // Track Mixpanel event - guard against empty userId
      if (userId && !isUnsupportedAsset) {
        // Determine CTA variant based on score: score >= 80 = 'talk_the_trade', else = 'publish_trade_score'
        const ctaVariant =
          score >= 80 ? 'talk_the_trade' : 'publish_trade_score';

        trackMixpanel({
          event: EventTypes.TRADE_SCORE_VIEWED,
          properties: {
            trade_id: tradeId,
            user_id: userId,
            trade_score: score,
            risk_score: metrics.risk,
            precision_score: metrics.precision,
            payoff_score: metrics.payoff,
            cta_variant: ctaVariant,
          },
        });
      }
    },
    [clearCloseTimeout, tradeId, userId, score, metrics, isUnsupportedAsset],
  );

  const handleTagMouseLeave = React.useCallback(() => {
    isHoveringRef.current = false;
    scheduleClose();
  }, [scheduleClose]);

  const handlePopoverMouseEnter = React.useCallback(() => {
    clearCloseTimeout();
    isHoveringRef.current = true;
  }, [clearCloseTimeout]);

  const handlePopoverMouseLeave = React.useCallback(() => {
    isHoveringRef.current = false;
    scheduleClose();
  }, [scheduleClose]);

  React.useEffect(() => {
    return () => {
      clearCloseTimeout();
    };
  }, [clearCloseTimeout]);

  const open = Boolean(anchorEl);
  const scoreColors = getScoreColor(score, theme);

  const publishClickHandler = createHandlePublishClick(
    navigate,
    sidebarStore,
    t,
    tradeId,
    symbol,
    metadata,
  );

  const handlePublishClick = useCallback(
    (data: {
      score: number;
      metrics: { risk: number; precision: number; payoff: number };
      metadata?: Record<string, unknown>;
    }) => {
      publishClickHandler(data);
    },
    [publishClickHandler],
  );

  return (
    <div>
      <StyledStatusTag
        onMouseEnter={handlePopoverOpen}
        onMouseLeave={handleTagMouseLeave}
        label={<StyledStatusBox>{score}</StyledStatusBox>}
        size="small"
        sx={{
          borderColor: scoreColors.border,
          color: scoreColors.border,
          backgroundColor: scoreColors.background,
        }}
      />
      <Popover
        sx={{
          pointerEvents: 'none',
        }}
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        onClose={() => {
          isHoveringRef.current = false;
          setAnchorEl(null);
        }}
        disableRestoreFocus
        disableAutoFocus
        disableEnforceFocus
        slotProps={{
          paper: {
            onMouseEnter: handlePopoverMouseEnter,
            onMouseLeave: handlePopoverMouseLeave,
            sx: {
              pointerEvents: 'auto',
              backgroundColor: 'transparent',
              boxShadow: 'none',
            },
          },
        }}
      >
        <div
          onMouseEnter={handlePopoverMouseEnter}
          onMouseLeave={handlePopoverMouseLeave}
        >
          <TradeScoreCard
            score={score}
            metrics={metrics}
            variant={isMobile ? 'mobile' : 'desktop'}
            onPublishClick={handlePublishClick}
            metadata={{
              tradeId,
              symbol,
              ...metadata,
            }}
            userId={userId}
            tradeId={tradeId}
            isUnsupportedAsset={isUnsupportedAsset}
            t={t}
          />
        </div>
      </Popover>
    </div>
  );
};

export const createTradesTableColumns = (
  isMobile = false,
  t: TFunction<'dashboard'>,
  tradingSystem?: TradingSystem,
  enableTradeScore = false,
  userId = '',
): GridColDef[] => {
  const tPosition = i18next.getFixedT(null, 'position');
  const widths = isMobile ? COLUMN_WIDTHS.mobile : COLUMN_WIDTHS.desktop;
  const defaultConfig = getDefaultColumnConfig();

  const columns: GridColDef[] = [
    {
      ...defaultConfig,
      field: 'id',
      headerName: getHeaderName('id', t, 'ID'),
      sortable: true,
      width: widths.id,
      renderCell: (params: GridRenderCellParams) => (
        <StyledCellBox>{params.row.id || params.row.externalId}</StyledCellBox>
      ),
    },
    {
      ...defaultConfig,
      field: 'symbol',
      headerName: getHeaderName('symbol', t, 'Symbol'),
      width: widths.symbol,
      sortable: true,
    },
    {
      ...defaultConfig,
      field: 'side',
      headerName: getHeaderName('side', t, 'Side'),
      width: widths.side,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        let color: TAG_COLORS = TAG_COLORS.ERROR; // Default for Sell
        if (params.value === PositionSide.Buy) {
          color = TAG_COLORS.SUCCESS;
        } else if (params.value === PositionSide.Correction) {
          color = TAG_COLORS.INFO;
        }
        return (
          <StyledSideTag
            label={params.value}
            color={color as ChipProps['color']}
            size="small"
          />
        );
      },
    },
    {
      ...defaultConfig,
      field: 'openDate',
      headerName: getHeaderName('openDate', t, 'Open Date'),
      width: widths.openDate,
      sortable: true,
      renderCell: (params: GridRenderCellParams) =>
        formatBrokerDate(params.value, DATE_FORMAT, tradingSystem),
    },
    {
      ...defaultConfig,
      field: 'closeDate',
      headerName: getHeaderName('closeDate', t, 'Close Date'),
      width: widths.closeDate,
      sortable: true,
      renderCell: (params: GridRenderCellParams) => {
        // For open positions, closeDate might be null/undefined
        if (!params.row.isClosed || !params.value) {
          return null;
        }
        return formatBrokerDate(params.value, DATE_FORMAT, tradingSystem);
      },
    },
    {
      ...defaultConfig,
      field: 'entry',
      headerName: getHeaderName('entry', t, 'Entry'),
      width: widths.entry,
      type: 'number',
      renderCell: (params: GridRenderCellParams) =>
        renderCurrencyWithTooltip(
          params.value,
          getHeaderName('entry', t, 'Entry'),
        ),
    },
    {
      ...defaultConfig,
      field: 'exit',
      headerName: getHeaderName('exit', t, 'Exit'),
      width: widths.exit,
      type: 'number',
      renderCell: (params: GridRenderCellParams) => {
        // For open positions, exit might be null/undefined
        if (!params.row.isClosed) {
          return null;
        }
        return renderCurrencyWithTooltip(
          params.value,
          getHeaderName('exit', t, 'Exit'),
        );
      },
    },
    {
      ...defaultConfig,
      field: 'quantity',
      headerName: getHeaderName('quantity', t, 'Qty'),
      width: widths.quantity,
      type: 'number',
    },
    {
      ...defaultConfig,
      field: 'fee',
      headerName: getHeaderName('fee', t, 'Fee'),
      width: widths.fee,
      type: 'number',
      renderCell: (params: GridRenderCellParams) =>
        formatCurrencyStandard(params.value),
    },
    {
      ...defaultConfig,
      field: 'swap',
      headerName: getHeaderName('swap', t, 'Swap'),
      width: widths.swap,
      type: 'number',
      renderCell: (params: GridRenderCellParams) =>
        formatCurrencyStandard(params.value),
    },
    {
      ...defaultConfig,
      field: 'profitAndLoss',
      headerName: getHeaderName('profitAndLoss', t, 'P&L'),
      width: widths.profitAndLoss,
      sortable: true,
      type: 'number',
      renderCell: (params: GridRenderCellParams) => {
        // Don't show P&L for open positions
        if (!params.row.isClosed) {
          return null;
        }

        return (
          <StyledPnLBox errorColor={!params.row.isValid}>
            {formatCurrencyStandard(params.value)}
          </StyledPnLBox>
        );
      },
    },
    {
      ...defaultConfig,
      field: 'status',
      headerName: getHeaderName('status', t, 'Status'),
      sortable: false,
      width: widths.status,
      renderCell: (params: GridRenderCellParams) => {
        // Check if this is an open position
        if (!params.row.isClosed) {
          return null;
        }

        const isInvalid =
          params.row.isValid === false &&
          params.row.invalidReasons &&
          params.row.invalidReasons.length > 0;

        if (isInvalid) {
          const invalidReasonsText = formatInvalidReasons(
            params.row.invalidReasons,
            params.row.minTradeDuration,
            tPosition,
          );
          const tooltipPrefix = t(
            'tradesTable.invalidTradeTooltipPrefix',
            'This trade was marked invalid due to the following:',
          );
          const tooltipContent = `${tooltipPrefix} ${invalidReasonsText}`;

          return (
            <Tooltip title={tooltipContent}>
              <span>
                <StyledStatusTag
                  label={
                    <StyledStatusBox>
                      <SystemIcon iconName="Info" size={12} />
                    </StyledStatusBox>
                  }
                  size="small"
                  color={TAG_COLORS.WARNING}
                />
              </span>
            </Tooltip>
          );
        }

        const isWin = params.row.profitAndLoss > 0;
        const statusEnum = isWin
          ? ClosedPositionStatus.Win
          : ClosedPositionStatus.Loss;
        const status = t(
          `tradesTable.status.${statusEnum.toLowerCase()}`,
          statusEnum,
        );

        return (
          <StyledStatusTag
            label={
              <StyledStatusBox>
                <SystemIcon
                  iconName={isWin ? 'ArrowUp' : 'ArrowDown'}
                  size={12}
                />
                {status}
              </StyledStatusBox>
            }
            size="small"
            color={isWin ? TAG_COLORS.SUCCESS : TAG_COLORS.ERROR}
          />
        );
      },
    },
  ];

  if (enableTradeScore) {
    columns.push({
      ...defaultConfig,
      field: 'tradeScore',
      headerName: getHeaderName('tradeScore', t, 'Trade Score'),
      width: widths.tradeScore,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const hasTradeScore = params.row?.tradeScore !== undefined;
        if (!hasTradeScore) {
          return '-';
        }

        const score = params.row?.tradeScore?.finalScore || 0;
        const formatMetric = (value: number | undefined): number => {
          const metricValue = value || 0;
          return metricValue <= 1 ? Math.round(metricValue * 100) : metricValue;
        };

        const metrics = {
          risk: formatMetric(params.row?.tradeScore?.riskScore),
          precision: formatMetric(params.row?.tradeScore?.precisionScore),
          payoff: formatMetric(params.row?.tradeScore?.payoffScore),
        };

        const tradeId = params.row?.id || params.row?.externalId || '';

        return (
          <TradeScoreCell
            score={score}
            metrics={metrics}
            isMobile={isMobile}
            tradeId={tradeId}
            symbol={params.row.symbol}
            metadata={{
              openDate: params.row.openDate,
              closeDate: params.row.closeDate,
              side: params.row.side,
              profitAndLoss: params.row.profitAndLoss,
            }}
            userId={userId}
            t={t}
          />
        );
      },
    });
  }

  return columns;
};
